import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, ChargeList, ConsentSwitch, EmptyState, SavedCardSummary, Skeleton, StatusChip } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { dayMonthTime, formatMoney } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { IN_STAY_CONSENT_TEXT_VERSION, pagosService } from '@/services/pagos'
import type { InStayConsent } from '@/services/pagos'
import { money } from '@/services/types'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink, PAY_ROUTES, usePagosStay } from './shared'
import { holderTraveler } from '@/services/reserva/holder'

/**
 * 8.3 · Tarjeta guardada y cargos (Pagos.dc.html screen=card, Bloque 3 §3g). Tarjeta real (E2 `validCardId`);
 * consentimiento explícito y revocable que solo cuenta si lo registra el backend: sin endpoint se muestra
 * "Pendiente de confirmar" y no se habilitan cargos (D37);
 * cargos contra mock, vacío en real (D36).
 */
export function Tarjeta() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = usePagosStay()
  const [info, reloadInfo] = useAsync(`tarjeta:${stay.guestCode}:${apiLang}`, (signal) => pagosService.getSavedCard(stay, apiLang, signal))
  const [charges, reloadCharges] = useAsync(`cargos:${stay.guestCode}`, (signal) => pagosService.getCharges(stay, signal))
  const [consent, setConsent] = useState<InStayConsent | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const card = info.kind === 'ready' ? info.value : null
  const current = consent ?? card?.consent ?? null
  const pending = current?.status === 'pending_confirmation'

  // Solo cuenta lo que registra el backend (D37): el interruptor refleja la respuesta, no la intención.
  const toggle = (granted: boolean) => {
    setSaving(true)
    setSaveError(null)
    const holder = holderTraveler(stay)
    pagosService
      .setInStayConsent(stay, { granted, textVersion: IN_STAY_CONSENT_TEXT_VERSION, text: `${t('v2.pagos.tarjeta.permitir')}. ${t('v2.pagos.tarjeta.permitirTexto')}`, lang: i18n.language, guestId: holder?.id ?? null, cardId: card?.card.id ?? '' })
      .then(setConsent)
      .catch(() => setSaveError(t('v2.legacy.error.tryAgainOrContactUs')))
      .finally(() => setSaving(false))
  }

  const items = charges.kind === 'ready' ? charges.value : []
  const total = items.reduce((sum, c) => sum + c.amount.amount, 0)

  return (
    <main className="page pagos">
      <div className="pagos__head">
        <BackLink to={PAY_ROUTES.payments} label={t('v2.pagos.pendiente.titulo')} />
        <h1>{t('v2.pagos.tarjeta.titulo')}</h1>
        <p className="page__lead">{t(charges.kind === 'unsupported' ? 'v2.pagos.tarjeta.leadSinCargos' : 'v2.pagos.tarjeta.lead')}</p>
      </div>
      {info.kind === 'loading' && (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={150} />
          <Skeleton height={200} />
        </div>
      )}
      {info.kind === 'error' && <SectionError code={info.code} onRetry={reloadInfo} />}
      {info.kind === 'ready' && !card && <EmptyState icon="credit-card" title={t('v2.pagos.tarjeta.sinTarjeta')} headingLevel={2} />}
      {card && (
        <>
          <Card>
            <div className="pagos__stack">
              <SavedCardSummary brand={card.card.brand} last4={card.card.last4} expiry={card.card.expiry} holder={card.card.holder} />
              <div className="pagos__switch-divider">
                <ConsentSwitch checked={current?.status === 'granted'} onChange={toggle} disabled={pending || saving} title={t('v2.pagos.tarjeta.permitir')} description={t(pending ? 'v2.pagos.tarjeta.permitirTextoNeutro' : 'v2.pagos.tarjeta.permitirTexto')} />
              </div>
              <div aria-live="polite" className="pagos__group">
                {pending && (
                  <>
                    <StatusChip tone="warning">{t('v2.pagos.tarjeta.pendienteConfirmar')}</StatusChip>
                  </>
                )}
                {!pending && current?.recordedAt && (
                  <p className="pagos__note">{t(current.status === 'granted' ? 'v2.pagos.tarjeta.registrado' : 'v2.pagos.tarjeta.retirado', { date: dayMonthTime(current.recordedAt, lang) })}</p>
                )}
                {saveError && (
                  <p className="pagos__error" role="alert">
                    {saveError}
                  </p>
                )}
              </div>
            </div>
          </Card>
          {pending && <NotAvailable consequences={[t('v2.noDisponible.consecuencia.cargos')]} />}
          {charges.kind !== 'unsupported' && <h2 className="pagos__h2">{t('v2.pagos.tarjeta.cargos')}</h2>}
          {charges.kind === 'loading' && (
            <div aria-busy="true">
              <Skeleton height={180} />
            </div>
          )}
          {charges.kind === 'error' && <SectionError code={charges.code} onRetry={reloadCharges} />}
          {charges.kind === 'ready' && items.length === 0 && (
            <EmptyState icon="shopping-bag" title={t('v2.pagos.tarjeta.sinCargos')} headingLevel={3}>
              {t('v2.pagos.tarjeta.sinCargosTexto')}
            </EmptyState>
          )}
          {items.length > 0 && (
            <Card flush>
              <ChargeList
                items={items.map((c) => ({ id: c.id, concept: c.concept, meta: c.receiptSent ? t('v2.pagos.tarjeta.cargoMeta', { date: dayMonthTime(c.date, lang) }) : dayMonthTime(c.date, lang), amount: formatMoney(c.amount, lang) }))}
                totalLabel={t('v2.pagos.tarjeta.totalCargado')}
                total={formatMoney(money(total, items[0]?.amount.currency ?? stay.currency), lang)}
              />
            </Card>
          )}
        </>
      )}
    </main>
  )
}
